
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from './components/Sidebar';
import { httpsCallable, firebaseFunctions } from './services/firebaseService';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useCafData } from './hooks/useCafData';
import { hasSecondFactor, isSsoAccount } from './utils/mfa';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Lazy Load Views
const LoginView = React.lazy(() => import('./views/LoginView'));
const MfaSetupView = React.lazy(() => import('./views/MfaSetupView'));
const UserManagementView = React.lazy(() => import('./views/UserManagementView'));
const DashboardView = React.lazy(() => import('./views/DashboardView').then(module => ({ default: module.DashboardView })));
const LearningView = React.lazy(() => import('./views/LearningView'));
const AssessmentView = React.lazy(() => import('./views/AssessmentView'));
const PrioritizationView = React.lazy(() => import('./views/PrioritizationView'));
const PortfolioView = React.lazy(() => import('./views/PortfolioView'));
const ProjectDetailView = React.lazy(() => import('./views/ProjectDetailView'));
const ContentManagementView = React.lazy(() => import('./views/ContentManagementView'));
const SuperAdminView = React.lazy(() => import('./views/SuperAdminView'));
const AssessorDashboardView = React.lazy(() => import('./views/AssessorDashboardView'));
const SetPasswordView = React.lazy(() => import('./views/SetPasswordView'));
const FinishAssessorRegistrationView = React.lazy(() => import('./views/FinishAssessorRegistrationView'));
const SettingsView = React.lazy(() => import('./views/SettingsView'));
const MyTasksView = React.lazy(() => import('./views/MyTasksView'));

// Public Legal Views
const PrivacyView = React.lazy(() => import('./views/PrivacyView'));
const ImpressumView = React.lazy(() => import('./views/ImpressumView'));
const TermsView = React.lazy(() => import('./views/TermsView'));
const CookiePolicyView = React.lazy(() => import('./views/CookiePolicyView'));

import { MenuIcon, ExclamationTriangleIcon, LockClosedIcon } from './components/icons';
import { ConnectionStatusScreen } from './components/ConnectionStatusScreen';
import ConfirmationModal from './components/ConfirmationModal';
import { AutoLogout } from './components/AutoLogout';
import { Logo } from './components/Logo';
import LanguageUnavailableModal from './components/LanguageUnavailableModal';
import { UpdatePrompt } from './components/UpdatePrompt';
import { useLiveAnnouncer } from './contexts/LiveAnnouncerContext';
import { useLandmarkNavigation } from './hooks/useLandmarkNavigation';

const MainLayout: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);
    const { currentUser, userProfile, isSuperAdmin, isAssessor, permissions, profileLoadFailed, isRetryingProfile, retryProfileLoad, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { announce } = useLiveAnnouncer();
    useLandmarkNavigation();
    const isInitialRender = useRef(true);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // WCAG: Announce route changes to screen readers and move focus to main content.
    // Skips the initial render to avoid announcing the first page load.
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        // Map pathnames to human-readable page names
        const routeNames: Record<string, string> = {
            '/dashboard': t('sidebar.dashboard'),
            '/learning': t('sidebar.learningHub'),
            '/assessment': t('sidebar.assessment'),
            '/prioritization': t('sidebar.prioritization'),
            '/portfolio': t('sidebar.portfolio'),
            '/my-tasks': t('sidebar.myTasks'),
            '/admin/content': t('sidebar.contentManagement'),
            '/admin/settings': t('sidebar.settings'),
            '/admin/users': t('sidebar.userManagement'),
            '/super-admin': t('sidebar.superAdmin'),
        };
        // Projektseiten (/portfolio/:projectId/...) gehören zum Portfolio
        const pageName = routeNames[location.pathname]
            || (location.pathname.startsWith('/portfolio/') ? t('sidebar.portfolio') : location.pathname);
        announce(pageName);

        // Move focus to the main content area for keyboard users
        const mainEl = document.getElementById('main-content');
        if (mainEl) {
            mainEl.focus({ preventScroll: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Navigation Guard State
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);

    // Language Availability Guard
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    // Tracks the language explicitly chosen by the user in the modal (before Firestore confirms it).
    // This prevents the Sync-Language effect from reverting i18n back to the invalid old language.
    const userSelectedLangRef = React.useRef<string | null>(null);

    // SSO users are exempt — identity verification handled by IdP (ssoUser claim in Firestore rules)
    const isSecondFactorVerified = isSsoAccount(currentUser) || hasSecondFactor(currentUser);

    // Use Custom Hook for Data Logic
    const {
        cafStructure,
        cafInfoModules,
        methods,
        tenantConfig,
        isLoading,
        isTenantConfigLoaded,
        isTenantLocked,
        saveCafStructure,
        saveCafInfoModules,
        saveMethods,
        saveTenantConfig
    } = useCafData(userProfile, isSuperAdmin, isSecondFactorVerified);

    const tenantId = userProfile?.tenantId;

    // MFA Enforcement Logic
    const isMfaEnforced = !!tenantId && tenantConfig?.enableMFA === true;
    const isMfaMissing = isMfaEnforced && !isSuperAdmin && !isSecondFactorVerified;
    // Freiwillige Einrichtung (z.B. aus den Einstellungen): danach und beim Abbrechen dorthin zurück
    const requestedReturn = (location.state as { returnTo?: string } | null)?.returnTo;
    const mfaReturnTo = requestedReturn?.startsWith('/') && !requestedReturn.startsWith('//') ? requestedReturn : '/dashboard';

    // Guard: Enforce MFA
    useEffect(() => {
        if (!isLoading && currentUser && isMfaMissing && location.pathname !== '/mfa-setup') {
            navigate('/mfa-setup');
        }
    }, [isLoading, currentUser, isMfaMissing, location.pathname, navigate]);

    // Sync App Language with User Profile Setting.
    // Skip if the user just picked a new language in the modal — prevents reverting i18n to the
    // invalid old profile language before the Firestore profile update propagates back.
    useEffect(() => {
        if (userSelectedLangRef.current) return;  // language already switched locally, wait for profile update
        if (userProfile?.language && userProfile.language !== i18n.language) {
            i18n.changeLanguage(userProfile.language);
        }
    }, [userProfile?.language, i18n]);

    // Detect if user's saved language is no longer in the tenant's allowed list.
    // When userProfile.language finally updates (Firestore onSnapshot), clear the override ref.
    useEffect(() => {
        if (!userProfile || isSuperAdmin) return;
        const available = tenantConfig?.availableLanguages;
        if (!available || available.length === 0) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setShowLanguageModal(false);
            return;
        }
        const isInvalid = !!userProfile.language && !available.includes(userProfile.language);
        if (!isInvalid) {
            // Language is now valid (profile updated in Firestore) → clear the override ref
            userSelectedLangRef.current = null;
            setShowLanguageModal(false);
        } else if (!userSelectedLangRef.current) {
            // Only show the modal if the user hasn't already made a selection in this session
            setShowLanguageModal(true);
        }
    }, [userProfile, tenantConfig?.availableLanguages, isSuperAdmin]);

    // Guard: Initial Redirect based on Role
    useEffect(() => {
        if (!isLoading && location.pathname === '/') {
            if (isSuperAdmin) {
                navigate('/super-admin');
            } else if (isAssessor) {
                navigate('/dashboard'); // Assessor dashboard
            } else if (permissions.canUseCaf) {
                navigate('/dashboard');
            } else if (permissions.canUseProjects) {
                navigate('/portfolio');
            } else if (permissions.isTenantAdmin) {
                navigate('/admin/settings');
            } else if (permissions.isOrgAdmin) {
                navigate('/admin/users');
            } else {
                navigate('/portfolio');
            }
        }
    }, [isSuperAdmin, isAssessor, isLoading, location.pathname, navigate, permissions]);

    // Custom Navigation Handler (replaces simple Navigate)
    // Used by Sidebar to check for unsaved changes
    const handleNavigation = (path: string) => {
        if (location.pathname === path) {
            setIsMobileMenuOpen(false);
            return;
        }

        if (hasUnsavedChanges) {
            setPendingPath(path);
            setShowUnsavedModal(true);
        } else {
            navigate(path);
            setIsMobileMenuOpen(false);
        }
    };

    const confirmNavigation = () => {
        if (pendingPath) {
            setHasUnsavedChanges(false);
            navigate(pendingPath);
            setPendingPath(null);
            setIsMobileMenuOpen(false);
        }
        setShowUnsavedModal(false);
    };

    const cancelNavigation = () => {
        setPendingPath(null);
        setShowUnsavedModal(false);
    };

    // -- RENDER BLOCKS --

    // Mandant gesperrt (settenantstatus): Das Profil kommt per onSnapshot, die Seite erscheint sofort. Regeln und
    // Functions weisen ab diesem Moment alles ab; die Anmeldung ist mit dem deaktivierten Konto nicht mehr möglich.
    // isTenantLocked: Mandant gesperrt, Profil ohne Merker (Sperre von früher, nur isActive: false im Mandanten)
    if (userProfile?.tenantLocked === true || isTenantLocked) {
        return (
            <main className="min-h-screen bg-gray-50 flex items-center justify-center p-4" aria-labelledby="tenant-locked-title">
                <div className="bg-white max-w-md w-full rounded-2xl shadow-xl p-8 text-center animate-fade-in">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <LockClosedIcon className="w-8 h-8 text-red-600" />
                    </div>
                    <h1 id="tenant-locked-title" className="text-2xl font-bold text-gray-900 mb-2">{t('tenantLock.page.title')}</h1>
                    <p className="text-gray-600 mb-8">{t('tenantLock.page.message')}</p>
                    <button
                        onClick={() => logout()}
                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full transition-colors"
                    >
                        {t('tenantLock.page.logout')}
                    </button>
                </div>
            </main>
        );
    }

    if (userProfile?.isActive === false) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white max-w-md w-full rounded-2xl shadow-xl p-8 text-center animate-fade-in">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <LockClosedIcon className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('app.errors.accountDisabled.title', 'Account gesperrt / Inaktiv')}</h2>
                    <p className="text-gray-600 mb-8">
                        {t('app.errors.accountDisabled.message', 'Ihr Benutzerkonto wurde deaktiviert. Bitte kontaktieren Sie den Support oder Ihren Administrator für weitere Informationen.')}
                    </p>
                    <button
                        onClick={() => logout()}
                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full transition-colors"
                    >
                        {t('app.errors.accountDisabled.logout', 'Abmelden')}
                    </button>
                </div>
            </div>
        );
    }

    // Profil wegen der Verbindung nicht geladen: nicht "keine Organisation" melden
    if (profileLoadFailed) {
        return <ConnectionStatusScreen variant="failed" onRetry={retryProfileLoad} isRetrying={isRetryingProfile} onLogout={() => logout()} />;
    }

    if (!tenantId && !isLoading && !isSuperAdmin && location.pathname !== '/super-admin') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
                <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md">
                    <h2 className="text-xl font-bold text-red-600 mb-2">{t('app.errors.noTenant.title')}</h2>
                    <p className="text-gray-600 mb-6">
                        {t('app.errors.noTenant.message')}
                    </p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => logout()}
                            className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-900"
                        >
                            {t('sidebar.logout')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Bis die Mandantenkonfiguration da ist, keine Routen auswerten (sonst leiten direkte Links fälschlich um)
    if (isLoading || (tenantId && !isSuperAdmin && !isTenantConfigLoaded)) {
        return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-400 rounded-full"></div>{t('app.loading')}</div>;
    }

    const mfaSetup = (
        <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <MfaSetupView
                isEnforced={isMfaMissing}
                completeLabel={mfaReturnTo === '/dashboard' ? undefined : t('common.next')}
                onComplete={async () => {
                    await currentUser?.reload();
                    navigate(mfaReturnTo);
                }}
                onCancel={() => {
                    if (isMfaMissing) {
                        logout();
                    } else {
                        navigate(mfaReturnTo);
                    }
                }}
            />
        </div>
    );

    // MFA-Pflicht ohne zweiten Faktor: nur die Einrichtung. Bis dahin geben die Regeln ausser der Konfiguration
    // nichts heraus; Seitennavigation und Ansichten stellten nur Abfragen, die abgelehnt werden.
    if (isMfaMissing) {
        return (
            <main id="main-content" tabIndex={-1} className="min-h-screen bg-gray-50 p-4 outline-none" aria-label={t('common.mainContent', 'Hauptinhalt')}>
                <AutoLogout />
                <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-400 rounded-full"></div></div>}>
                    {mfaSetup}
                </React.Suspense>
            </main>
        );
    }

    return (
        <div className="flex h-screen bg-gray-50 font-sans text-gray-900 overflow-hidden">
            {/* Skip to Content — WCAG 2.4.1 Bypass Blocks */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-bold focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
                {t('common.skipToContent', 'Zum Inhalt springen')}
            </a>
            {/* Language Unavailability Guard Modal */}
            {showLanguageModal && tenantConfig?.availableLanguages && tenantConfig.availableLanguages.length > 0 && (
                <LanguageUnavailableModal
                    availableLanguages={tenantConfig.availableLanguages}
                    onLanguageSelected={(lang) => {
                        userSelectedLangRef.current = lang; // Prevent Sync-Language effect from reverting
                        setShowLanguageModal(false);
                    }}
                />
            )}
            {/* Sidebar */}
            <Sidebar
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                activeView={location.pathname as any} // Temporary cast until Sidebar is updated
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onViewChange={handleNavigation as any} // Renaming prop in next step
                mobileOpen={isMobileMenuOpen}
                onMobileClose={() => setIsMobileMenuOpen(false)}
                prioritizationEnabled={tenantConfig.enablePrioritization || false}
                contentManagementEnabled={tenantConfig.enableContentManagement || false}
                taskManagementEnabled={tenantConfig.enableTaskManagement || false}
                isMfaEnforced={isMfaEnforced}
                availableLanguages={tenantConfig.availableLanguages}
            />

            <AutoLogout />

            {/* Main Layout */}
            <div className="flex-1 flex flex-col h-full overflow-hidden relative">
                {/* Mobile Header */}
                <header className="lg:hidden bg-white border-b border-gray-200 p-4 flex justify-between items-center sticky top-0 z-10 shadow-sm">
                    <div className="flex items-center space-x-3">
                        <Logo className="w-8 h-8" />
                        <h1 className="text-lg font-bold text-gray-800 tracking-tight">{t('sidebar.title')}</h1>
                    </div>
                    <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" aria-label={t('common.openMenu', 'Menü öffnen')}>
                        <MenuIcon className="w-6 h-6" />
                    </button>
                </header>

                {/* Main Content Area */}
                <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto outline-none" aria-label={t('common.mainContent', 'Hauptinhalt')}>
                    <div className="max-w-7xl mx-auto">
                        <React.Suspense fallback={<div className="h-full flex items-center justify-center min-h-[50vh]"><div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-400 rounded-full"></div></div>}>
                            {(() => {
                                const isTenantAdminOrHigher = permissions.isTenantAdmin;
                                const isOrgAdminOrHigher = isTenantAdminOrHigher || permissions.isOrgAdmin;
                                const isQmOrManagement = permissions.canUseCaf;
                                const canUseProjects = permissions.canUseProjects;

                                return (
                                    <Routes>
                                        <Route path="/dashboard" element={
                                            (isAssessor) ? <AssessorDashboardView onNavigate={(p) => navigate(p === 'assessment' ? '/assessment' : p)} /> :
                                                (!isSuperAdmin && tenantId && isQmOrManagement) ? <DashboardView cafStructure={cafStructure} onNavigate={(v) => navigate(`/${v}`)} aiAnalysisEnabled={tenantConfig.aiAnalysisEnabled} /> : <Navigate to={isSuperAdmin ? '/super-admin' : '/'} />
                                        } />

                                        <Route path="/learning" element={
                                            (!isSuperAdmin && !isAssessor && tenantId && isQmOrManagement) ? <LearningView
                                                cafStructure={cafStructure}
                                                cafInfoModules={cafInfoModules}
                                                methods={methods}
                                                setHasUnsavedChanges={setHasUnsavedChanges}
                                                aiAnalysisEnabled={tenantConfig.aiAnalysisEnabled}
                                            /> : <Navigate to="/" />
                                        } />

                                        <Route path="/assessment" element={
                                            (!isSuperAdmin && (tenantId || isAssessor) && (isAssessor || isQmOrManagement)) ? <AssessmentView
                                                cafStructure={cafStructure}
                                                setHasUnsavedChanges={setHasUnsavedChanges}
                                                aiAnalysisEnabled={tenantConfig.aiAnalysisEnabled}
                                                customCollectionPath={isAssessor && userProfile ? `assessor_workspaces/${userProfile.uid}/assessments` : undefined}
                                                methods={methods}
                                            /> : <Navigate to="/" />
                                        } />

                                        <Route path="/prioritization" element={
                                            (!isSuperAdmin && !isAssessor && tenantId && tenantConfig.enablePrioritization && isQmOrManagement) ?
                                                <PrioritizationView cafStructure={cafStructure} aiAnalysisEnabled={tenantConfig.aiAnalysisEnabled} methods={methods} /> : <Navigate to="/" />
                                        } />

                                        {/* Aufgaben und zugewiesene Projekte: auch für persönlich zugewiesene Personen ohne Projektrolle (docs/07, 5.3) */}
                                        <Route path="/my-tasks" element={
                                            (!isSuperAdmin && !isAssessor && tenantId && tenantConfig.enableTaskManagement) ?
                                                <MyTasksView /> : <Navigate to="/" />
                                        } />

                                        <Route path="/portfolio" element={
                                            (!isSuperAdmin && !isAssessor && tenantId && canUseProjects) ?
                                                <PortfolioView cafStructure={cafStructure} enableTaskManagement={tenantConfig.enableTaskManagement} enableLegacyPortfolioAxes={tenantConfig.enableLegacyPortfolioAxes} tenantConfig={tenantConfig} /> : <Navigate to="/" />
                                        } />

                                        <Route path="/portfolio/:projectId/*" element={
                                            (!isSuperAdmin && !isAssessor && tenantId) ?
                                                <ProjectDetailView cafStructure={cafStructure} enableTaskManagement={tenantConfig.enableTaskManagement} setHasUnsavedChanges={setHasUnsavedChanges} tenantConfig={tenantConfig} /> : <Navigate to="/" />
                                        } />

                                        <Route path="/admin/content" element={
                                            ((isSuperAdmin || (isTenantAdminOrHigher && tenantConfig.enableContentManagement)) && !isAssessor) ?
                                                <ContentManagementView
                                                    cafStructure={cafStructure}
                                                    cafInfoModules={cafInfoModules}
                                                    methods={methods}
                                                    onSave={saveCafStructure}
                                                    onSaveInfoModules={saveCafInfoModules}
                                                    onSaveMethods={saveMethods}
                                                    setHasUnsavedChanges={setHasUnsavedChanges}
                                                    customTitle={isSuperAdmin ? t('app.globalTemplatesTitle') : undefined}
                                                    customIntro={isSuperAdmin ? t('app.globalTemplatesIntro') : undefined}
                                                    canEditInfoModules={isSuperAdmin}
                                                /> : <Navigate to="/" />
                                        } />

                                        <Route path="/admin/users" element={
                                            (isOrgAdminOrHigher && !isSuperAdmin && !isAssessor && tenantId) ? <UserManagementView /> : <Navigate to="/" />
                                        } />

                                        <Route path="/admin/settings" element={
                                            // Mandantenadmin: alle Einstellungen; Portfoliomanagement: Kategorien der eigenen Einheit
                                            ((isTenantAdminOrHigher || permissions.canManagePortfolio) && !isSuperAdmin && !isAssessor) ? <SettingsView config={tenantConfig} onSave={saveTenantConfig} tenantId={tenantId} /> : <Navigate to="/" />
                                        } />

                                        <Route path="/mfa-setup" element={mfaSetup} />

                                        <Route path="/super-admin" element={
                                            (isSuperAdmin) ? <SuperAdminView /> : <Navigate to="/" />
                                        } />

                                        <Route path="/" element={<div />} /> {/* Redirect handled by effect */}
                                        <Route path="*" element={<div>{t('app.viewNotFound')}</div>} />
                                    </Routes>
                                );
                            })()}
                        </React.Suspense>
                    </div>
                </main>
            </div>

            <ConfirmationModal
                isOpen={showUnsavedModal}
                onClose={cancelNavigation}
                onConfirm={confirmNavigation}
                title={t('app.navigationGuard.title')}
                message={
                    <div className="space-y-2">
                        <p>{t('app.navigationGuard.message')}</p>
                        <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded border border-amber-100 font-medium">
                            {t('app.navigationGuard.warning')}
                        </p>
                    </div>
                }
                confirmButtonText={t('app.navigationGuard.confirmButton')}
                confirmButtonClass="bg-amber-700 hover:bg-amber-800 focus:ring-amber-500"
                icon={ExclamationTriangleIcon}
            />
        </div>
    );
};

const App: React.FC = () => {
    const { currentUser, loading, isAssessor, logout } = useAuth();
    const { t } = useTranslation();
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resetParams, setResetParams] = useState<{ mode: string, oobCode?: string, token?: string, email?: string } | null>(null);

    useEffect(() => {
        // Die Sprache aus ?lang= (Einladung, Passwort zurücksetzen) setzt i18n.ts schon vor der ersten Darstellung
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');
        const oobCode = params.get('oobCode');
        const token = params.get('token');
        const email = params.get('email') || undefined;

        if ((mode === 'resetPassword' || mode === 'finishAssessor') && (oobCode || token)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setResetParams({ mode, oobCode: oobCode || undefined, token: token || undefined, email });
        }
    }, []);

    if (resetParams) {
        if (resetParams.mode === 'finishAssessor') {
            return (
                <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-400 rounded-full"></div></div>}>
                    <FinishAssessorRegistrationView
                        oobCode={resetParams.oobCode}
                        token={resetParams.token}
                        initialEmail={resetParams.email}
                        onSuccess={() => {
                            window.history.replaceState({}, document.title, window.location.pathname);
                            setResetParams(null);
                        }}
                    />
                </React.Suspense>
            );
        }
        return (
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-400 rounded-full"></div></div>}>
                <SetPasswordView
                    oobCode={resetParams.oobCode}
                    token={resetParams.token}
                    initialEmail={resetParams.email}
                    onSuccess={() => {
                        window.history.replaceState({}, document.title, window.location.pathname);
                        setResetParams(null);
                    }}
                />
            </React.Suspense>
        );
    }

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-100">{t('app.loading')}</div>;

    if (!currentUser) {
        return (
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-400 rounded-full"></div></div>}>
                <Routes>
                    <Route path="/datenschutz" element={<PrivacyView />} />
                    <Route path="/impressum" element={<ImpressumView />} />
                    <Route path="/nutzungsbedingungen" element={<TermsView />} />
                    <Route path="/cookie-richtlinie" element={<CookiePolicyView />} />
                    <Route path="*" element={<LoginView />} />
                </Routes>
            </React.Suspense>
        );
    }

    const handleResend = async () => {
        setResendLoading(true);
        try {
            if (!firebaseFunctions) throw new Error(t('app.connection.failedTitle'));
            const resendFn = httpsCallable(firebaseFunctions, 'resendVerificationEmail');
            await resendFn();
            setResendSuccess(true);
        } catch (error: unknown) {
            console.error("Resend failed", error);
            if (error instanceof Error) {
                // Das Backend meldet fachliche Fehler als Übersetzungsschlüssel (backend.errors.*)
                alert(t('app.errors.emailSendFailed', 'Fehler beim Senden: {{msg}}', { msg: t(error.message, { defaultValue: error.message }) }));
            } else {
                alert(t('app.errors.emailSendUnknown', 'Fehler beim Senden: Unbekannter Fehler'));
            }
        } finally {
            setResendLoading(false);
        }
    };

    if (isAssessor && !currentUser.emailVerified) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 font-sans">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-gray-100">
                    {/* ... (Existing Email verification content) ... */}
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{t('app.verifyEmail.title', 'E-Mail Bestätigung erforderlich')}</h2>
                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                        {t('app.verifyEmail.prompt', 'Bitte bestätigen Sie Ihre E-Mail-Adresse')} <strong>{currentUser.email}</strong>, {t('app.verifyEmail.spamFolder', 'um fortzufahren. Überprüfen Sie auch Ihren Spam-Ordner.')}
                    </p>

                    <button
                        onClick={() => window.location.reload()}
                        className="w-full bg-blue-600 text-white py-2.5 rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-sm mb-3"
                    >
                        {t('app.verifyEmail.confirmReload', 'Ich habe bestätigt (Seite neu laden)')}
                    </button>

                    {resendSuccess ? (
                        <div className="text-green-600 text-sm font-medium mb-3">
                            {t('app.verifyEmail.successMessage', 'E-Mail wurde erneut gesendet!')}
                        </div>
                    ) : (
                        <button
                            onClick={handleResend}
                            disabled={resendLoading}
                            className="w-full text-blue-600 hover:text-blue-800 text-sm font-bold mb-6 disabled:opacity-50"
                        >
                            {resendLoading ? t('common.sending', 'Sende...') : t('app.verifyEmail.resendLink', 'Bestätigungs-Link erneut senden')}
                        </button>
                    )}

                    <div className="border-t border-gray-100 pt-4">
                        <button
                            onClick={() => logout()}
                            className="text-gray-500 hover:text-gray-800 text-sm font-medium"
                        >
                            {t('sidebar.logout')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return <MainLayout />;
};

import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { turnstileAppCheckProviderInstance } from './services/TurnstileAppCheckProvider';
import { USE_EMULATORS } from './services/firebaseService';

const AppWithProvider: React.FC = () => {
    // Read sitekey from env, fallback to Cloudflare testing key
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
    
    const turnstileRef = useRef<TurnstileInstance>(null);

    useEffect(() => {
        turnstileAppCheckProviderInstance.setResetCallback(() => {
            turnstileRef.current?.reset();
        });
    }, []);
    
    return (
        <>
            {/* 
              CRITICAL: Turnstile must be mounted OUTSIDE of AuthProvider.
              AuthProvider blocks rendering until it fetches data from Firestore.
              Firestore requires an App Check token from Turnstile.
              If Turnstile is inside AuthProvider, they deadlock and force a timeout!
            */}
            {!USE_EMULATORS && (
                <div id="turnstile-container">
                    <Turnstile
                        ref={turnstileRef}
                        siteKey={siteKey}
                        onSuccess={(token) => turnstileAppCheckProviderInstance.setTurnstileToken(token)}
                        onExpire={() => turnstileAppCheckProviderInstance.clearTurnstileToken()}
                        onError={(code) => {
                            console.error("Turnstile encounters an error:", code);
                            turnstileAppCheckProviderInstance.markTurnstileError();
                        }}
                    />
                </div>
            )}
            <UpdatePrompt />
            <AuthProvider>
                <App />
            </AuthProvider>
        </>
    );
};

export default AppWithProvider;
